<script setup lang="ts">
/**
 * Slideover for managing item images: upload, reorder, rotate, delete.
 * Custom drop zone feeds files into Uppy for optional editing and upload.
 * vuedraggable handles drag-to-reorder on both desktop and mobile.
 */
import { router } from "@inertiajs/vue3"
import Uppy from "@uppy/core"
import ImageEditor from "@uppy/image-editor"
import Dashboard from "@uppy/vue/dashboard"
import XHRUpload from "@uppy/xhr-upload"
import { onBeforeUnmount, ref, watch } from "vue"
import draggable from "vuedraggable"

import "@uppy/core/css/style.min.css"
import "@uppy/dashboard/css/style.min.css"
import "@uppy/image-editor/css/style.min.css"
import CoverBadge from "@/components/CoverBadge.vue"
import DropZone from "@/components/DropZone.vue"
import ImageActionButton from "@/components/ImageActionButton.vue"
import ImageActionOverlay from "@/components/ImageActionOverlay.vue"
import InlineEmpty from "@/components/InlineEmpty.vue"
import ContainerImageDestinationModal from "@/components/items/ContainerImageDestinationModal.vue"
import {
  type ImageUploadMeta,
  useContainerImageDestinationPrompt,
} from "@/composables/useContainerImageDestinationPrompt"
import { csrfToken, deleteJson, patchJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import { DEFAULT_IMAGE_EDITOR_ACTIONS } from "@/lib/uppy"
import {
  editItemPath,
  itemImagePath,
  itemImageRotationPath,
  itemImagesPath,
  reorderItemImagesPath,
} from "@/routes"
import type { ImageSummary } from "@/types/models"

const props = defineProps<{
  open: boolean
  itemId: string
  images: ImageSummary[]
  isContainer: boolean
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const localImages = ref<ImageSummary[]>([])
const lastOrder = ref<string[]>([])
const rotating = ref<string | null>(null)
const hasUppyFiles = ref(false)
const dirty = ref(false)
const createdChildren = ref<{ id: string; name: string }[]>([])

watch(
  () => props.open,
  (open) => {
    if (open) {
      localImages.value = [...props.images]
      lastOrder.value = props.images.map((img) => img.id)
      dirty.value = false
      createdChildren.value = []
    }
  },
)

interface ImageUploadResponse {
  [key: string]: unknown
  id: string
  child_item?: { id: string; name: string }
}

const uppy = new Uppy<ImageUploadMeta, ImageUploadResponse>({
  restrictions: {
    maxFileSize: 10 * 1024 * 1024,
    allowedFileTypes: ["image/jpeg", "image/png", "image/webp"],
  },
})

uppy.use(ImageEditor, {
  actions: DEFAULT_IMAGE_EDITOR_ACTIONS,
})

uppy.use(XHRUpload, {
  endpoint: itemImagesPath(props.itemId),
  method: "POST",
  fieldName: "image",
  headers: { "X-CSRF-Token": csrfToken() },
})

const { promptFile, chooseDestination } = useContainerImageDestinationPrompt(
  uppy,
  props.isContainer,
)

uppy.on("file-added", () => {
  hasUppyFiles.value = uppy.getFiles().length > 0
})
uppy.on("file-removed", () => {
  hasUppyFiles.value = uppy.getFiles().length > 0
})

uppy.on("complete", (result) => {
  const successful = result.successful ?? []
  if (successful.length > 0) {
    const children = successful.flatMap((file) => {
      const child = file.response?.body?.child_item
      return child ? [child] : []
    })
    createdChildren.value.push(...children)
    uppy.clear()
    hasUppyFiles.value = false
    dirty.value = true
    router.reload({
      onSuccess: () => {
        localImages.value = [...props.images]
      },
    })
  }
})

onBeforeUnmount(() => {
  uppy.destroy()
})

function addFiles(files: FileList) {
  for (const file of files) {
    try {
      uppy.addFile({ source: "local", name: file.name, type: file.type, data: file })
    } catch {
      // Restriction errors surfaced by Uppy's UI
    }
  }
}

async function onReorder() {
  const imageIds = localImages.value.map((img) => img.id)
  if (imageIds.every((id, i) => id === lastOrder.value[i])) return

  const res = await patchJson(reorderItemImagesPath(props.itemId), { image_ids: imageIds })
  if (res.ok) {
    const data = await res.json()
    localImages.value = data.images
    lastOrder.value = data.images.map((img: ImageSummary) => img.id)
    dirty.value = true
  }
}

async function rotateImage(imageId: string) {
  rotating.value = imageId
  const res = await patchJson(itemImageRotationPath(props.itemId, imageId), { direction: "cw" })
  if (res.ok) {
    const updated = await res.json()
    const idx = localImages.value.findIndex((img) => img.id === imageId)
    if (idx !== -1) localImages.value[idx] = updated
    dirty.value = true
  }
  rotating.value = null
}

async function deleteImage(imageId: string) {
  const res = await deleteJson(itemImagePath(props.itemId, imageId))
  if (res.ok) {
    const data = await res.json()
    localImages.value = data.images
    dirty.value = true
  }
}

function close() {
  emit("update:open", false)
  if (dirty.value) router.reload()
}
</script>

<template>
  <USlideover :open="open" title="Manage Photos" @update:open="emit('update:open', $event)">
    <template #body>
      <div class="flex flex-col gap-6">
        <DropZone
          v-if="!hasUppyFiles"
          :icon="ICON.imagePlus"
          label="Add photos"
          note="JPG, PNG or WebP, up to 10 MB"
          accept="image/jpeg,image/png,image/webp"
          @files="addFiles"
        />

        <Dashboard
          v-else
          :uppy="uppy"
          :props="{
            proudlyDisplayPoweredByUppy: false,
            note: 'JPG, PNG or WebP, up to 10 MB',
            height: 300,
          }"
        />

        <UAlert
          v-for="child in createdChildren"
          :key="child.id"
          :icon="ICON.item"
          color="success"
          variant="subtle"
          :title="`${child.name} was created inside this container`"
          description="The photo is attached to the new item. Rename it now if needed."
        >
          <template #actions>
            <UButton
              data-rename-item
              label="Rename item"
              color="neutral"
              variant="outline"
              size="xs"
              @click="router.visit(editItemPath({ id: child.id }))"
            />
          </template>
        </UAlert>

        <div v-if="localImages.length > 0">
          <p class="text-muted mb-2 text-xs">Drag to reorder. First image is the cover photo.</p>

          <draggable
            v-model="localImages"
            item-key="id"
            class="grid grid-cols-2 gap-2 sm:grid-cols-3"
            ghost-class="opacity-40"
            @end="onReorder"
          >
            <template #item="{ element, index }">
              <div
                class="group relative aspect-square cursor-grab overflow-hidden rounded-lg active:cursor-grabbing"
              >
                <img
                  :src="element.thumb_url ?? undefined"
                  :alt="`Photo ${index + 1}`"
                  class="size-full object-cover"
                  :class="{ 'animate-pulse opacity-50': rotating === element.id }"
                />

                <CoverBadge v-if="index === 0" />

                <ImageActionOverlay :force-visible="rotating === element.id">
                  <ImageActionButton
                    :icon="ICON.rotate"
                    title="Rotate"
                    :disabled="rotating === element.id"
                    @click.stop="rotateImage(element.id)"
                  />
                  <ImageActionButton
                    :icon="ICON.delete"
                    title="Delete"
                    color="text-red-600"
                    @click.stop="deleteImage(element.id)"
                  />
                </ImageActionOverlay>
              </div>
            </template>
          </draggable>
        </div>

        <InlineEmpty v-else :icon="ICON.item" message="No photos yet" icon-size="size-12" />
      </div>
    </template>

    <template #footer>
      <UButton label="Done" color="primary" block @click="close" />
    </template>
  </USlideover>

  <ContainerImageDestinationModal
    v-if="promptFile"
    :open="true"
    :file-name="promptFile.name"
    :preview-url="promptFile.previewUrl"
    @choose="chooseDestination"
  />
</template>
